import { AppContext } from "App/AppContext";
import { EMPTY_ITEM_RIGHTS_COLLECTION } from "Global/Base/Rights/RightTypes";
import { debounce } from "Global/Base/Utils/FunctionUtils";
import { ValidationErrorType } from "OW.Remoting/Basic/EOWExceptionsInterfaces";
import { EOWUserException, EOWValidateException } from "OW.Remoting/Basic/OWExceptions";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { HasPositiveID } from "OW.Remoting/Remoting/DataTypes/utils";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { SaveResultData } from "UI/UI.Controls/DataSource/EditableDBDataSource";
import { ControlRightTypesGroup } from "UI/UI/Rights/RightGroups";
import { FormControlWithLoggerParams, IAttributeControl, IControl } from "UI/UI/UI/Controls/ControlInterfaces";
import { Edit } from "UI/UI/UI/Controls/Edit";
import { UIManager } from "UI/UI/UI/UIManager";
import { SeparateDetailFrame } from "UI/UI/UI/XGControls/SeparateDetailFrame";
import { SharedMethods } from "UI/UI/UI/XGControls/SharedMethods";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Caption } from "../../UI/Controls/Caption";

/**
 * CustomControl pro změnu uživatelských údajů.
 */
export class UserInfoSettings extends SeparateDetailFrame implements IDisposable, IControl {
    private _userNameEdit: Edit;
    private _emailEdit: Edit;
    private _phoneEdit: Edit;
    private _buttons: HTMLElement;
    private _cancelButton: HTMLButtonElement;
    private _saveButton: HTMLButtonElement;
    private _inputChanged: boolean;
    private _emailValidation: string = "^[a-zA-Z0-9_\\-\\.]+@[a-zA-Z0-9_\\-\\.]+$";
    private _phoneValidation: string = "^(\\+\\d{1,4})?( ?\\d{1,4}){1,4}$";

    constructor(params: FormControlWithLoggerParams) {
        super(params);
        this.element.setAttribute("data-behaviour", "frame");
        this.element.setAttribute("data-behaviour-subtype", "detail");
        this._buildSections();
        this._registerListeners();
    }

    /**
     * Aktualizuje stav tlačítek.
     */
    private async _updateButtons(): Promise<void> {
        if (this.actualObject?.canEdit && (this._userNameEdit.enabled || this._emailEdit.enabled || this._phoneEdit.enabled)) {
            this._buttons.classList.remove("hidden");
            this._saveButton.disabled = !this.actualObject?.isChanged && !this._inputChanged;
            this._cancelButton.disabled = !this.actualObject?.isChanged && !this._inputChanged;
        } else {
            this._buttons.classList.add("hidden");
            this._saveButton.disabled = true;
            this._cancelButton.disabled = true;
        }
    }

    /**
     * Spustí akci pod loaderem.
     * @param action Akce, která se má spustit.
     */
    private async _executeAction(action: () => Promise<boolean | OWObject>): Promise<void> {
        UIManager.instance.showLoader();
        try {
            await action();
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Registruje obsluhu tlačítek a změny údajů.
     */
    private _registerListeners(): void {
        this._events.addListener(this._saveButton, "click", async () => {
            await this._executeAction(() => this.saveRecordClick());
        });

        this._events.addListener(this._cancelButton, "click", async () => {
            await this._executeAction(() => this.actualizeData());
        });

        this._events.addListener(this._userNameEdit.innerElement, "input", () => this._onInputChange());
        this._events.addListener(this._emailEdit.innerElement, "input", () => this._onInputChange());
        this._events.addListener(this._phoneEdit.innerElement, "input", () => this._onInputChange());

        this._events.add(this.rightsManager.rightsEvaluated, () => this._updateButtons());
    }

    @debounce(200, false)
    private _onInputChange(): void {
        this._inputChanged = true;
        this._updateButtons();
    }

    /**
     * Vytváří html strukturu komponenty.
     */
    private _buildSections(): void {
        this.container = createElement({ classes: "user-info-settings-container" });
        const wrapper = createElement({ classes: "wrapper" });
        const userInfoSection = createElement({ type: "section" });

        const header = userInfoSection.appendChild(createElement({ type: "h3" }));
        header.appendChild(createElement({ type: "span", classes: ["icon", "user-single-neutral-actions-edit"] }));
        const headerText = header.appendChild(createElement({ type: "span" }));
        Localization.addLocData(headerText, "GUI-780334"); // Osobní údaje

        const userInfoInputComponentClasses = [ "user-info-input-component" ];

        const userNameElement = createElement({
            classes: userInfoInputComponentClasses,
            attrs: {
                "data-attrname": "Nazev",
                "data-captionlockey": "GUI-780336", //Jméno
                "data-captiontop": "110",
                "data-captionleft": "20",
                "data-behaviour": "component",
            },
        });
        this._userNameEdit = new Edit({ element: userNameElement, originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION });
        this._userNameEdit.enabled = false;

        const emailElement = createElement({
            classes: userInfoInputComponentClasses,
            attrs: {
                "data-attrname": "Email",
                "data-captionlockey": "GUI-780338", // Email
                "data-captiontop": "110",
                "data-captionleft": "20",
                "data-behaviour": "component",
                "data-ValidationExpr": this._emailValidation,
            },
        });
        this._emailEdit = new Edit({ element: emailElement, originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION });
        this._emailEdit.enabled = false;

        const phoneElement = createElement({
            classes: userInfoInputComponentClasses,
            attrs: {
                "data-attrname": "Telefon",
                "data-captionlockey": "GUI-780337", // Telefon
                "data-captiontop": "110",
                "data-captionleft": "20",
                "data-behaviour": "component",
                "data-ValidationExpr": this._phoneValidation,
            },
        });
        this._phoneEdit = new Edit({ element: phoneElement, originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION });
        this._phoneEdit.enabled = false;

        userInfoSection.appendChild(userNameElement);
        userInfoSection.appendChild(emailElement);
        userInfoSection.appendChild(phoneElement);
        wrapper.appendChild(userInfoSection);

        this._buttons = createElement({ classes: "bottom-buttons hidden" });

        this._cancelButton = this._buttons.appendChild(createElement({
            id: UIManager.instance.generateElementID(),
            type: "button",
            classes: [ "primary", "btn", "btn-large", "loc-loading" ],
            attrs: { "data-id": "cancel-button-user-info" },
        })) as HTMLButtonElement;

        this._cancelButton.disabled = true;
        Localization.addLocData(this._cancelButton, "GUI-7"); // Zrušit změny

        this._saveButton = this._buttons.appendChild(createElement({
            id: UIManager.instance.generateElementID(),
            type: "button",
            classes: [ "primary", "btn", "btn-large", "loc-loading" ],
            attrs: { "data-id": "save-button-user-info" },
        })) as HTMLButtonElement;

        this._saveButton.disabled = true;
        Localization.addLocData(this._saveButton, "GUI-781275"); // Změnit údaje

        wrapper.appendChild(this._buttons);
        this.container.appendChild(wrapper);
        this.element.appendChild(this.container);

        Caption.createCaptionForFirstChild(this._userNameEdit.element);
        Caption.createCaptionForFirstChild(this._emailEdit.element);
        Caption.createCaptionForFirstChild(this._phoneEdit.element);
    }

    /**
     * Reaguje na změnu hodnoty atributu
     * @param sender Komponenta, která událost vyvolala.
     */
    protected override async _valueChange(sender: IAttributeControl): Promise<void> {
        await super._valueChange(sender);
        this._updateButtons();
    }

    /**
     * Funkce vrací HTML prvek reprezentující tuto tříu.
     **/
    protected _getMainContainerElement(): HTMLElement {
        return document.querySelector("UserInfoSettings");
    }

    /**
     * Načte objekt s údaji o uživateli
     * @returns Načtený objekt.
     */
    public override async actualizeData(): Promise<OWObject> {
        await Promise.all([this.waitForPendingUpdates(), this.waitForPendingActions()]);

        this.validationManager.clearErrors(null);
        const userObject = await OWManager.getObject(AppContext.instance.currentUser.id, "Cuzivatel");
        const personBridge = await Promise.all([userObject.getObjectAttributeID("Osoba_bridge"), userObject.getObjectAttributeClass("Osoba_bridge")]);
        if (HasPositiveID(personBridge[0])) {
            const userInfoObject = await OWManager.getObject(personBridge[0], personBridge[1]);
            this.objectName = userInfoObject.objectName;
            this._setActualObject(userInfoObject);
            this.isEditing = userInfoObject.canEdit;
            this._inputChanged = false;
        } else {
            this._userNameEdit.enabled = false;
            this._userNameEdit.enabledVisual = false;
            this._emailEdit.enabled = false;
            this._emailEdit.enabledVisual = false;
            this._phoneEdit.enabled = false;
            this._phoneEdit.enabledVisual = false;
            this.isEditing = false;
        }
        return this.actualObject;
    }

    /**
     * Ukládá objekt s údaji o uživateli
     * @returns True pokud se uložení povedlo, jinak false.
     */
    public override async saveRecordClick(): Promise<boolean> {
        await Promise.all([this.waitForPendingUpdates(), this.waitForPendingActions()]);

        if (this.actualObject) {
            this._validationManager.clearErrors(null, ValidationErrorType.postponedError);
            this._addValidationErrosForLoadedAttrs();
        }

        let saved = false;
        if (await this.validate(true, true)) {
            await this._newEvaluateRightsGroup(ControlRightTypesGroup.beforeSave);

            const saveResult: SaveResultData[] = [];

            try {
                saveResult.push({
                    data: this.actualObject,
                    messages: await this.actualObject.applyUpdates(),
                });
                saved = true;
            } catch (e) {
                if (e instanceof EOWValidateException)
                    saveResult.push({ data: this.actualObject, errors: e.validationErrors });
                else if (e instanceof EOWUserException)
                    saveResult.push({ data: this.actualObject, messages: e.resultMessages });
                else
                    throw e;
            }

            this._handleSaveErrors(saveResult);
            SharedMethods.processSaveResultsAndShowMessages(this, saveResult); // Zobrazuje všechny chyby kromě validačních

            if (saved) {
                this._inputChanged = false;
                this._evaluateRightsOnActualObjectChange();
            }
        }
        SharedMethods.showValidationMessages(this.validationManager); // Zobrazuje jenom validační chyby
        return saved;
    }
}
